/**
 * AI CONTEXT
 * Purpose: user admin table with create/edit and disabled-user toggle.
 * Key deps: useUserTableData, AddUserFormModal, TableFiltersRow.
 * Referenced by: App route ROUTES.USERS (permission-gated).
 * Importance: controls identity/role access across app.
 * Refactor: persist toggle preference + share CRUD table modal pattern.
 */
import { Checkbox } from "antd"
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  USER_SORTABLE_FIELDS,
  USER_TABLE_COLUMNS,
} from "#constants/userOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type UserForTable } from "#customTypes/composedTypes/userTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import AddUserFormModal from "#forms/AddUser/AddUserFormModal"
import useUserTableData from "#hooks/dataHooks/useUserTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const UsersTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<Uuid | undefined>()

  const {
    usersForTable,
    onSearch,
    showDisabledUsers,
    setShowDisabledUsers,
    isReady,
    sortField,
    sortOrder,
    onSortChange,
  } = useUserTableData(selectedUserId)

  const canSeeUsers = useHasPermission(RESOURCES.USER, USER_ACTIONS.READ)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const columns = useMemo(
    () =>
      buildTableColumns<UserForTable>(USER_TABLE_COLUMNS, {
        sortableFields: USER_SORTABLE_FIELDS,
        showId: canViewId,
      }),
    [canViewId]
  )

  const handleOpen = () => {
    if (!canSeeUsers) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedUserId(undefined)
  }

  const handleRowClick = async (selectedUserId: Uuid): Promise<void> => {
    setSelectedUserId(selectedUserId)
    handleOpen()
  }

  if (!canSeeUsers) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Users</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search by name, email, role"
          settingsKey={SETTINGS_KEYS.USERS}
        />

        <div>
          <Checkbox
            onChange={(e) => setShowDisabledUsers(e.target.checked)}
            checked={showDisabledUsers}
            style={{
              userSelect: "none",
              margin: "8px 0",
              transform: "scale(1.1)",
              transformOrigin: "top left",
            }}
          >
            Show disabled users
          </Checkbox>
        </div>

        {isOpen && canSeeUsers && (
          <AddUserFormModal onClose={handleClose} userId={selectedUserId} />
        )}
        <Table2<UserForTable>
          isLoading={!isReady}
          data={usersForTable}
          columns={columns}
          onRowClick={handleRowClick}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default UsersTable
